import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from "firebase/functions";
import { functions } from '../firebase';

const claimStravaClubCredits = httpsCallable(functions, 'claimStravaClubCredits');
const getCreditRewardStatus = httpsCallable(functions, 'getCreditRewardStatus');

function StravaClubCard({ user, posthog, onRewardClaimed, variant = 'default' }) {
    const { t } = useTranslation();
    const [showForm, setShowForm] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [isAlreadyClaimed, setIsAlreadyClaimed] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const checkInProgressRef = useRef(false);

    const STRAVA_CLUB_URL = "https://www.strava.com/clubs/1635526";
    const STORAGE_KEY = 'strava_club_join_pending';

    useEffect(() => {
        checkIfAlreadyClaimed();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        // Auto-check membership if user previously clicked join button
        const checkPendingJoin = async () => {
            if (!user || isAlreadyClaimed || checkInProgressRef.current) return;
            
            const pendingJoin = localStorage.getItem(STORAGE_KEY);
            if (pendingJoin === user.uid && !isChecking) {
                // User previously clicked join, auto-check membership
                setShowForm(true);
                
                // Wait a bit longer to ensure everything is loaded and avoid conflicts
                setTimeout(() => {
                    if (!checkInProgressRef.current && !isAlreadyClaimed) {
                        handleVerifyMembership(true);
                    }
                }, 2000);
            }
        };
        
        // Check on mount
        checkPendingJoin();
        
        // Check when tab gains focus or becomes visible
        const handleFocus = () => {
            checkPendingJoin();
        };
        
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                checkPendingJoin();
            }
        };
        
        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, isAlreadyClaimed, isChecking]);

    useEffect(() => {
        // Track when user views the Strava club card
        if (user && isAlreadyClaimed !== null) {
            posthog?.capture('strava_club_card_viewed', {
                page: 'credits',
                userId: user?.uid,
                alreadyClaimed: isAlreadyClaimed
            });
        }
    }, [user, isAlreadyClaimed, posthog]);

    const checkIfAlreadyClaimed = async () => {
        if (!user) return;

        try {
            const result = await getCreditRewardStatus();
            if (result.data?.stravaClub?.claimed) {
                setIsAlreadyClaimed(true);
                
                // Clear any pending state if already claimed
                if (user.uid) {
                    localStorage.removeItem(STORAGE_KEY);
                }
            }
        } catch (error) {
            console.error("Error checking reward status:", error);
        }
    };

    const handleJoinClubClick = () => {
        // Track the click
        posthog?.capture('strava_club_join_clicked', {
            page: 'credits',
            userId: user?.uid
        });

        // Save pending state to localStorage
        if (user?.uid) {
            localStorage.setItem(STORAGE_KEY, user.uid);
        }

        // Open the Strava club page in a new tab
        window.open(STRAVA_CLUB_URL, '_blank');

        // Show the verify button
        setShowForm(true);
    };

    const handleVerifyMembership = async (isAutoCheck = false) => {
        // Prevent concurrent calls
        if (checkInProgressRef.current) {
            console.log('Verification already in progress, skipping...');
            return;
        }

        checkInProgressRef.current = true;
        setIsChecking(true);
        setError('');

        // Track verification attempt
        posthog?.capture('strava_club_verify_attempted', {
            page: 'credits',
            userId: user?.uid,
            isAutoCheck: isAutoCheck
        });

        try {
            const result = await claimStravaClubCredits();

            if (result.data.success) {
                setSuccess(true);
                setIsAlreadyClaimed(true);

                // Clear pending state from localStorage
                if (user?.uid) {
                    localStorage.removeItem(STORAGE_KEY);
                }

                // Track success
                posthog?.capture('strava_club_credits_claimed', {
                    page: 'credits',
                    userId: user?.uid,
                    creditsAwarded: result.data.creditsAwarded,
                    isAutoCheck: isAutoCheck
                });

                // Notify parent to refresh credit count
                if (onRewardClaimed) {
                    onRewardClaimed();
                }
            } else {
                setError(result.data.error || 'Could not verify membership');
                
                // Track verification failure
                posthog?.capture('strava_club_verify_failed', {
                    page: 'credits',
                    userId: user?.uid,
                    error: result.data.error,
                    errorType: 'not_member'
                });
            }
        } catch (error) {
            console.error("Error claiming Strava club credits:", error);
            let errorType = 'unknown';
            
            if (error.code === 'unauthenticated' && error.message.includes('Strava access expired')) {
                setError('Your Strava connection has expired. Please reconnect your Strava account in Settings.');
                errorType = 'strava_expired';
            } else if (error.code === 'failed-precondition') {
                setError('Please connect your Strava account first in Settings.');
                errorType = 'strava_not_connected';
            } else {
                setError('Failed to verify membership. Please try again.');
                errorType = 'api_error';
            }
            
            // Track error
            posthog?.capture('strava_club_verify_error', {
                page: 'credits',
                userId: user?.uid,
                errorCode: error.code,
                errorMessage: error.message,
                errorType: errorType
            });
        } finally {
            setIsChecking(false);
            checkInProgressRef.current = false;
        }
    };

    // Use appropriate class based on variant
    const cardClass = variant === 'header' ? 'email-registration-card' : 'reward-card email-reward-card';

    if (isAlreadyClaimed) {
        return (
            <div className={`${cardClass} claimed`}>
                <div className={variant === 'header' ? 'email-registration-content' : 'reward-content'}>
                    <h4>{t('stravaClub.joinedTitle', 'Joined the Strava club')}</h4>
                    <p>{t('stravaClub.claimedDesc', "You've claimed your 5 credits!")}</p>
                    
                    <div className={variant === 'header' ? 'email-registration-form' : 'reward-form'}>
                        <button
                            className={variant === 'header' ? 'email-registration-button' : 'reward-button'}
                            disabled
                        >
                            {t('stravaClub.claimedButton', 'Credits Claimed')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={cardClass}>
            <div className={variant === 'header' ? 'email-registration-content' : 'reward-content'}>
                <h4>{t('stravaClub.getTitle', 'Get 5 Free Credits')}</h4>
                <p>{t('stravaClub.getDesc', 'Join our Strava Club for 5 free credits!')}</p>

                {!showForm ? (
                    <button
                        className={variant === 'header' ? 'email-registration-button' : 'reward-button'}
                        onClick={handleJoinClubClick}
                    >
                        {t('stravaClub.joinButton', 'Join Strava Club')}
                    </button>
                ) : (
                    <div style={{ marginTop: '16px' }}>
                        {success ? (
                            <div className="success-message" style={{
                                padding: '12px',
                                backgroundColor: 'var(--Success-50, #ECFDF5)',
                                border: '1px solid var(--Success-200, #A7F3D0)',
                                borderRadius: '8px',
                                color: 'var(--Success-700, #047857)',
                                marginBottom: '16px'
                            }}>
                                <i className="ti ti-circle-check" style={{ marginRight: '8px' }}></i>
                                Success! 5 credits added to your account.
                            </div>
                        ) : (
                            <>
                                <p style={{
                                    fontSize: '14px',
                                    color: 'var(--Gray-600)',
                                    marginBottom: '12px'
                                }}>
                                    {isChecking ? 
                                        t('stravaClub.checking', 'Checking your membership status...') : 
                                        t('stravaClub.verifyPrompt', 'Joined the club? Click below to verify your membership.')}
                                </p>

                                {error && (
                                    <div style={{
                                        padding: '12px',
                                        backgroundColor: 'var(--Error-50, #FEF2F2)',
                                        border: '1px solid var(--Error-200, #FECACA)',
                                        borderRadius: '8px',
                                        color: 'var(--Error-700, #B91C1C)',
                                        marginBottom: '16px',
                                        fontSize: '14px'
                                    }}>
                                        <i className="ti ti-alert-circle" style={{ marginRight: '8px' }}></i>
                                        {error}
                                    </div>
                                )}

                                <button
                                    className={variant === 'header' ? 'email-registration-button' : 'reward-button'}
                                    onClick={handleVerifyMembership}
                                    disabled={isChecking}
                                >
                                    {isChecking ? (
                                        <>
                                            <span className="loading-spinner-small" style={{ marginRight: '8px' }}></span>
                                            {t('stravaClub.verifying', 'Verifying...')}
                                        </>
                                    ) : (
                                        t('stravaClub.verifyButton', 'Verify Membership')
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default StravaClubCard;